import { ReactNode, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Lockup } from './Lockup';
import { isoWeek } from './score';
import { LangToggle, useI18n } from '../i18n';
import { useDocumentTitle } from './a11y';
import { useFocusHeadingOnNavigate } from './focus';
import { activeGrant, StaffContext } from '../lib/staffContext';
import { roleLabel } from './roles';

export type NavItem = { icon: string; label: string; href?: string; active?: boolean };

interface Props {
  role: string;
  nav: NavItem[];
  title: string;
  who?: string | null;
  /** Sign-in context: shows the active role, its school, the role switcher and the TEST badge. */
  ctx?: StaffContext | null;
  onSignOut: () => void;
  actions?: ReactNode;
  /** A full-width notice above the topbar on every screen (platform admin support mode). */
  banner?: ReactNode;
  children: ReactNode;
}

// The staff app frame from the design system: 14rem Sidebar (lockup, role
// overline, nav, scope + sign out), sticky glass Topbar, and the Page column.
// Below 760px the sidebar folds into a top bar (see styles.css).
//
// v2: the frame shows which role this sign-in is acting as ("Acting as"), with
// a role colour band along the top, so a person with two roles always knows
// which one is in action. People with more than one role get "Switch role".
// Test accounts carry a TEST badge on every screen.
export function StaffShell({ role, nav, title, who, ctx, onSignOut, actions, banner, children }: Props) {
  const { t } = useI18n();
  const h1 = useRef<HTMLHeadingElement>(null);
  useDocumentTitle(title);
  useFocusHeadingOnNavigate(h1);
  const { week, year } = isoWeek();

  const grant = ctx ? activeGrant(ctx) : undefined;
  const activeLabel = ctx?.active_role ? roleLabel(t, ctx.active_role) : role;
  const scope = grant?.school_name
    ? `${grant.school_name}${grant.school_code ? ` · ${grant.school_code}` : ''}`
    : grant?.municipality_name
      ? grant.municipality_name
      : ctx?.active_role === 'platform_admin'
        ? t['ctx.allSchools']
        : null;
  const canSwitch = (ctx?.roles.length ?? 0) > 1;

  return (
    <div className={`shell${ctx?.active_role ? ` role-${ctx.active_role}` : ''}`}>
      <a className="skip-link" href="#main">
        {t.skip}
      </a>
      <aside className="sidebar">
        <div className="sidebar-head">
          <Lockup />
        </div>
        <nav className="sidebar-nav" aria-label={activeLabel}>
          <p className="nav-label" aria-hidden="true">
            {activeLabel}
          </p>
          <ul className="nav-list">
            {nav.map((n) => (
              <li key={n.label}>
                {n.href?.startsWith('/') ? (
                  <Link
                    to={n.href}
                    className={`nav-item${n.active ? ' active' : ''}`}
                    aria-current={n.active ? 'page' : undefined}
                  >
                    <span aria-hidden="true">{n.icon}</span> {n.label}
                  </Link>
                ) : n.href ? (
                  <a
                    href={n.href}
                    className={`nav-item${n.active ? ' active' : ''}`}
                    aria-current={n.active ? 'page' : undefined}
                  >
                    <span aria-hidden="true">{n.icon}</span> {n.label}
                  </a>
                ) : (
                  <span className={`nav-item${n.active ? ' active' : ''}`} aria-current={n.active ? 'page' : undefined}>
                    <span aria-hidden="true">{n.icon}</span> {n.label}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </nav>
        <div className="sidebar-foot">
          {who && (
            <div className="sidebar-who">
              <p>
                {who}
                {ctx?.is_test && (
                  <>
                    {' '}
                    <span className="test-badge">{t['ctx.testBadge']}</span>
                  </>
                )}
              </p>
              <p>{activeLabel}</p>
              {scope && <p>{scope}</p>}
            </div>
          )}
          {canSwitch && (
            <Link className="nav-item nav-switch" to="/choose-role">
              {t['ctx.switchRole']}
            </Link>
          )}
          {(ctx?.active_role === 'platform_admin' || ctx?.mfa_applies) && (
            <Link className="nav-item nav-sub" to="/mfa">
              {t['mfa.navLink']}
            </Link>
          )}
          <Link className="nav-item nav-sub" to="/accessibility">
            {t['a11y.link']}
          </Link>
          <button type="button" className="nav-item signout" onClick={onSignOut}>
            {t.signOut}
          </button>
        </div>
      </aside>
      <div className="content">
        {banner}
        <header className="topbar">
          <h1 ref={h1} tabIndex={-1}>
            {title}
          </h1>
          <div className="topbar-right">
            {ctx?.active_role && (
              <span className="acting-as">
                <span className="text-muted">{t['ctx.actingAs']}</span> <strong>{activeLabel}</strong>
                {ctx.is_test && <span className="test-badge">{t['ctx.testBadge']}</span>}
              </span>
            )}
            {actions}
            {ctx && (
              <Link
                className={`bell${ctx.unread_notifications > 0 ? ' has-unread' : ''}`}
                to="/notifications"
                aria-label={
                  ctx.unread_notifications > 0 ? t['notif.bellUnread'](ctx.unread_notifications) : t['notif.bell']
                }
              >
                <span aria-hidden="true">🔔</span>
                {ctx.unread_notifications > 0 && (
                  <span className="bell-count" aria-hidden="true">
                    {ctx.unread_notifications > 99 ? '99+' : ctx.unread_notifications}
                  </span>
                )}
              </Link>
            )}
            <LangToggle />
            <span className="week-label">{t.week(week, year)}</span>
          </div>
        </header>
        <main id="main" tabIndex={-1} className="page fade-in">
          {children}
        </main>
      </div>
    </div>
  );
}
